// T-092: TS bridge per il plugin Android `ShareIntent` (vedi
// apps/mobile/android/app/src/main/java/it/trackeat/app/SharePlugin.java).
//
// Quando l'utente fa "Condividi" su un PDF/immagine da un'altra app del
// telefono e seleziona TrackEAT, MainActivity riceve l'Intent.ACTION_SEND,
// copia il file in cache app e salva (path, mime, name) in SharedPreferences.
// Questo modulo legge quei valori via plugin Capacitor e li fornisce al
// router/CapturePhotoPage.
//
// Design: polling (no listener push) — App.tsx chiama getPendingShare()
// al boot e a ogni resume; se non-null, naviga al wizard e poi pulisce.
// È più robusto del notifyListeners (che soffre race condition con la
// registrazione lato JS).

import { registerPlugin, Capacitor, type PluginListenerHandle } from '@capacitor/core';

export interface PendingShare {
  /** Path assoluto del file in app cache (es. /data/data/it.trackeat.app/cache/share/1716480000.pdf). */
  path: string;
  /** Mime type originale dell'intent (es. application/pdf, image/jpeg). */
  mime: string;
  /** Display name del file sorgente (best-effort, vuoto se non disponibile). */
  name: string;
}

interface ShareIntentPlugin {
  getPendingShare(): Promise<PendingShare | null>;
  clearPendingShare(): Promise<void>;
  /** Evento emesso da MainActivity quando arriva un Intent.ACTION_SEND mentre
   *  l'app è già viva (foreground o background). Per cold-start usare il
   *  polling getPendingShare(): l'evento non scatta perché il plugin non è
   *  ancora bridge-attached. */
  addListener(
    eventName: 'shareReceived',
    listenerFunc: (data: PendingShare) => void,
  ): Promise<PluginListenerHandle>;
}

const ShareIntent = registerPlugin<ShareIntentPlugin>('ShareIntent');

/** Ritorna il file condiviso pendente o null. Safe su web/iOS (nessuno-op). */
export async function getPendingShare(): Promise<PendingShare | null> {
  if (Capacitor.getPlatform() !== 'android') return null;
  try {
    const result = await ShareIntent.getPendingShare();
    // Capacitor restituisce {} se call.resolve() vuoto: normalizziamo.
    if (!result || !result.path) return null;
    return result;
  } catch (err) {
    console.warn('[shareIntent] getPendingShare failed', err);
    return null;
  }
}

/** Pulisce lo storage del plugin dopo che il file è stato consumato dal wizard. */
export async function clearPendingShare(): Promise<void> {
  if (Capacitor.getPlatform() !== 'android') return;
  try {
    await ShareIntent.clearPendingShare();
  } catch (err) {
    console.warn('[shareIntent] clearPendingShare failed', err);
  }
}

/**
 * Sottoscrive un handler per i nuovi share ricevuti mentre l'app è già viva.
 * Su web/iOS no-op (ritorna handle dummy). L'handler riceve PendingShare e
 * dovrebbe navigare a /documents/capture col file. Ricordarsi di chiamare
 * `.remove()` sul handle al cleanup del componente per evitare doppi
 * listener su HMR/remount.
 */
export async function onShareReceived(
  handler: (share: PendingShare) => void,
): Promise<PluginListenerHandle> {
  if (Capacitor.getPlatform() !== 'android') {
    // dummy handle no-op
    return { remove: async () => undefined };
  }
  return ShareIntent.addListener('shareReceived', handler);
}
